import React from 'react';
import { Box, HStack, Skeleton, Spacer, VStack } from 'native-base';
import {
    faAngleDown,
    faAngleUp,
    faGift,
    faTrophy,
} from '@fortawesome/free-solid-svg-icons';
import {
    MilestoneDTO,
    i18nFn,
    useAppSettings,
    useSpringBigStore,
    useTheme,
    resizedUrl,
} from '@springbig/shared';
import Image from '~/shared/Image';
import SliderBar from '~/shared/RangeBar';
import { Caption, H3 } from '~/shared/Typography';
import { SpIcon } from '~/shared/Icon';
import Pressable from '~/shared/Pressable';
import { Panel } from '~/shared/Theme';

type CardMilestoneProps = {
    currentValue?: number;
    milestone: MilestoneDTO;
    t: i18nFn;
    type: 'spend' | 'points' | 'visits';
};

const CardMilestone = ({
    currentValue,
    milestone,
    t,
    type,
}: CardMilestoneProps) => {

    const {
        primary,
        radius_md,
    } = useTheme();

    const [openMilestone, setOpenMilestone] =
        React.useState<boolean>(false);

    const { merchant } = useSpringBigStore();
    const { milestone_points_opacity } = useAppSettings();

    const getRewardNames = (): string => {

        return milestone?.rewards.map(reward => reward.item_name?.replace(/\| /g, '\n')).join(', ');
    };

    return (
        <Panel>
            <Pressable
                onPress={() => {

                    if (milestone?.rewards?.length) {
                        setOpenMilestone(!openMilestone);
                    }
                }}
            >
                <HStack justifyContent={'space-between'} w={'80%'}>
                    <HStack mb={2} alignItems={'center'}>
                        <Caption>
                            { milestone?.name }{' '}
                        </Caption>
                        { currentValue >= milestone.value &&
                            <SpIcon size={14} color={primary} icon={faTrophy} />
                        }
                    </HStack>
                    <Caption
                        fontSize={14}
                        opacity={milestone_points_opacity ? milestone_points_opacity : 0.5}
                    >
                        { currentValue <= milestone.value ? currentValue : milestone.value }/{ milestone.value }
                    </Caption>
                </HStack>
                <HStack>
                    <SliderBar defaultValue={currentValue} maxValue={milestone.value} w={'85%'} />
                    { milestone.rewards.length > 0 &&
                        <Pressable
                            w={'15%'}
                            flexDirection={'row'}
                            justifyContent={'flex-end'}
                            onPress={() => setOpenMilestone(!openMilestone)}
                        >
                            { openMilestone ?
                                <SpIcon color={primary} icon={faAngleUp} />
                                :
                                <SpIcon color={primary} icon={faAngleDown} />
                            }

                        </Pressable>
                    }
                </HStack>
                { milestone?.achieved ?
                    <Caption
                        fontSize={12}
                        fontWeight={'bold'}
                    >
                        {
                            milestone.rewards.length > 0 ?
                                t('milestones.achieved', [getRewardNames()])
                                :
                                t('milestones.milestone_achieved')
                        }
                    </Caption>
                    :
                    <Caption
                        fontSize={12}
                        fontWeight={'bold'}
                    >
                        {
                            type === 'spend' ? (
                                t('milestones.spends_title', [milestone.remaining, merchant?.name, getRewardNames()])
                            ) : (
                                t('milestones.points_title', [milestone.remaining, merchant?.name, getRewardNames()])
                            )
                        }
                    </Caption>
                }
                { openMilestone &&
                    <Box>
                        <H3 my={2}>
                            {
                                milestone?.achieved ? (
                                    t('milestones.congratulations', [milestone?.name])
                                ) : (
                                    type === 'spend' ? (
                                        t('milestones.spends_remaining', [milestone.remaining, getRewardNames()])
                                    ) : (
                                        t('milestones.points_remaining', [milestone.remaining, getRewardNames()])
                                    )
                                )
                            }
                        </H3>

                        <Box>
                            <HStack space={2} mt={2}>
                                <SpIcon
                                    color={primary}
                                    icon={faGift}
                                />
                                <Caption
                                    fontWeight={'semibold'}
                                    text={'milestones.benefits_title'}
                                />
                            </HStack>
                            { milestone?.rewards?.length && (
                                <Box mb={2} mt={2}>
                                    { milestone?.rewards?.map((reward, index) =>

                                        <HStack
                                            key={index}
                                            justifyContent={'space-between'}
                                            alignItems={'center'}
                                        >
                                            <Caption pl={6} fontWeight={'bold'}>
                                                { reward?.item_name?.replace(/\| /g, '\n') }
                                            </Caption>
                                            { reward.preload ?
                                                <Image
                                                    w={12}
                                                    h={12}
                                                    borderRadius={radius_md}
                                                    uri={resizedUrl(reward.preload, 48, 48)}
                                                />
                                                :
                                                <SpIcon color={primary} icon={faGift} size={40} />
                                            }
                                        </HStack>
                                    )}
                                </Box>
                            )}
                        </Box>
                    </Box>
                }
            </Pressable>
        </Panel>
    );
};

export const CardMilestoneSkeleton = () => {

    return (
        <VStack space={4} p={5} pr={'7rem'}>
            <HStack>
                <Skeleton h={2} rounded={'md'} flex={1} />
                <Spacer flex={1} />
                <Skeleton h={2} rounded={'md'} flex={1} />
            </HStack>
            <Skeleton h={2} rounded={'md'} w='100%' />
            <Skeleton width={'40%'} h={2} rounded={'md'} />

        </VStack>
    );
};

export default CardMilestone;
